import { Parse } from "parse";

Parse.initialize(
  process.env.REACT_APP_APP_ID,
  null,
  process.env.REACT_APP_MASTER_KEY
);
Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
Parse.serverURL = process.env.REACT_APP_SERVER_URL;

export const gameCoinsSummary = async (params) => {
  const filter = params;

  let user = Parse.User.current();
  user = await user.fetch();

  // ---------- Helpers ----------
  const dateMatchStage = (startDate, endDate) => {
    const match = {};
    if (startDate) match.$gte = new Date(startDate);
    if (endDate) {
      const end = new Date(endDate);
      end.setDate(end.getDate() + 1);
      match.$lt = end;
    }
    return Object.keys(match).length > 0 ? { $match: { gamePlayedDate: match } } : null;
  };

  const variantMatchStage = (variant) =>
    variant && variant !== "all" ? { $match: { type: variant } } : null;

  const gameNameMatchStage = (gameName) =>
    gameName && gameName.trim() !== ""
      ? { $match: { gameName: { $regex: gameName, $options: "i" } } }
      : null;

  // ---------- Match Stages ----------
  const pipeline = [];

  const dateMatch = dateMatchStage(filter.startDate, filter.endDate);
  const variantMatch = variantMatchStage(filter.gameVariant);
  const nameMatch = gameNameMatchStage(filter.gameName);

  if (dateMatch) pipeline.push(dateMatch);
  if (variantMatch) pipeline.push(variantMatch);
  if (nameMatch) pipeline.push(nameMatch);

  // Scope to current user
  pipeline.push({ $match: { _rperm: user.id } });

  // if manager is provided, also filter by manager
  if (filter.manager) {
    pipeline.push({ $match: { _rperm: filter.manager } });
  }
  // Use denormalized role
  // pipeline.push({ $match: { role: "Player" } });

  // Optional testing user filter
  if (!filter.includeTestingUsers) {
    pipeline.push({ $match: { isTestingUserData: { $ne: true } } });
  }

  // ---------- Summary Group ----------
  pipeline.push({
    $group: {
      _id: "$type", // TD or VE
      totalBetAmount: { $sum: "$totalBetAmount" },
      totalWinAmount: { $sum: "$totalWinAmount" },
    },
  });

  try {
    const result = await new Parse.Query("GameCoins").aggregate(pipeline, {
      useMasterKey: true,
    });

    console.log("✅ Aggregation Result:", result);

    let tadaBetAmount = 0;
    let tadaWinAmount = 0;
    let vegasBetAmount = 0;
    let vegasWinAmount = 0;

    for (const item of result) {
      if (item.objectId === "TD") {
        tadaBetAmount = item.totalBetAmount || 0;
        tadaWinAmount = item.totalWinAmount || 0;
      } else if (item.objectId === "VE") {
        vegasBetAmount = item.totalBetAmount || 0;
        vegasWinAmount = item.totalWinAmount || 0;
      }
    }

    return {
      tadaBetAmount,
      tadaWinAmount,
      vegasBetAmount,
      vegasWinAmount,
    };
  } catch (error) {
    console.error("❌ Aggregation error:", error);
    return { error: "Failed to fetch game-wise bet sum." };
  }
};

export const accountsSummary = async (params) => {
  try {
    const filter = params;
    let user = Parse.User.current();
    user = await user.fetch();

    // -------- Role Hierarchy --------
    const roleHierarchy = [
      "Super-User",
      "Game-Owner",
      "Master-Distributor",
      "Distributor",
      "Sub-Distributor",
      "Agent",
      "Player",
    ];

    const roleQuery = new Parse.Query(Parse.Role);
    roleQuery.equalTo("users", user);
    const currentRole = await roleQuery.first({ useMasterKey: true });

    let allowedRoleName = null;
    let allowedUsers = [];
    let totalCount = 0;

    if (currentRole) {
      const idx = roleHierarchy.indexOf(currentRole.get("name"));
      if (idx >= 0 && idx < roleHierarchy.length - 1) {
        allowedRoleName = roleHierarchy[idx + 1];

        // Override with filter.roleName if provided
        allowedRoleName =
          filter.roleName && filter.roleName !== "All"
            ? filter.roleName
            : allowedRoleName;

        const roleObj = await new Parse.Query(Parse.Role)
          .equalTo("name", allowedRoleName)
          .first({ useMasterKey: true });

        if (filter.manager) {
          // 🔹 Manager filter → fetch users directly
          const userQuery = new Parse.Query(Parse.User);
          userQuery.equalTo("objectId", filter.manager);
          userQuery.include("manager");
          userQuery.include("role");
          const users = await userQuery.find({ useMasterKey: true });

          allowedUsers = users.map((u) => ({
            id: u.id,
            username: u.get("username"),
            roleName: u.get("role")?.get("name") || "Unknown",
            managerName:
              u.get("manager")?.get("username") ||
              u.get("manager")?.get("name") ||
              null,
          }));

          totalCount = await userQuery.count({ useMasterKey: true });
        } else if (roleObj) {
          // 🔹 Role filter (default: next role below current)
          // Fetch ALL users for summary calculation (not paginated)
          const relation = roleObj.relation("users");
          const q = relation.query();
          q.limit(10000); // Fetch all users for summary
          q.include("manager");
          q.include("role");

          const users = await q.find({ useMasterKey: true });
          allowedUsers = users.map((u) => ({
            id: u.id,
            username: u.get("username"),
            roleName: u.get("role")?.get("name") || "Unknown",
            managerName: u.get("manager")?.get("username") || null,
          }));

          const relationCount = roleObj.relation("users");
          const qCount = relationCount.query();
          totalCount = await qCount.count({ useMasterKey: true });
        }
      }
    }

    // -------- Date Match Helper --------
    const dateMatch = (startDate, endDate, fieldName) => {
      const match = {};
      if (startDate) match.$gte = new Date(startDate);
      if (endDate) {
        const end = new Date(endDate);
        end.setDate(end.getDate() + 1);
        match.$lt = end;
      }
      return Object.keys(match).length > 0 ? { [fieldName]: match } : null;
    };

    const gameDateMatch = dateMatch(filter.startDate, filter.endDate, "txn_datetime");
    const gcStart = filter.startDate ? new Date(filter.startDate) : null;
    const gcEnd = filter.endDate
      ? (() => {
          const d = new Date(filter.endDate);
          d.setDate(d.getDate() + 1);
          return d;
        })()
      : null;

    // -------- Aggregate Totals Across All Allowed Users --------
    let totalRecharge = 0;
    let totalRedeem = 0;
    let netAmount = 0;
    let totalBetAmount = 0;
    let totalWinAmount = 0;

    for (const userInfo of allowedUsers) {
      const uid = userInfo.id;

      // 🔹 Transactions totals
      const txnPipeline = [
        ...(gameDateMatch ? [{ $match: gameDateMatch }] : []),
        { $match: { _rperm: uid } },
        {
          $group: {
            _id: null,
            totalRecharge: {
              $sum: { $cond: [{ $eq: ["$txn_type", 0] }, "$amount", 0] },
            },
            totalRedeem: {
              $sum: { $cond: [{ $eq: ["$txn_type", 1] }, "$amount", 0] },
            },
          },
        },
        {
          $project: {
            totalRecharge: 1,
            totalRedeem: 1,
            netAmount: { $subtract: ["$totalRecharge", "$totalRedeem"] },
          },
        },
      ];

      const txnData = await new Parse.Query("Transactions").aggregate(txnPipeline, { useMasterKey: true });
      const txn = txnData[0] || { totalRecharge: 0, totalRedeem: 0, netAmount: 0 };

      totalRecharge += txn.totalRecharge;
      totalRedeem += txn.totalRedeem;
      netAmount += txn.netAmount;

      // 🔹 GameCoins totals
      const gcPipeline = [
        { $match: { _rperm: uid } },
        ...(gcStart || gcEnd
          ? [
              {
                $match: {
                  ...(gcStart ? { createdAt: { $gte: gcStart } } : {}),
                  ...(gcEnd ? { createdAt: { $lt: gcEnd } } : {}),
                },
              },
            ]
          : []),
        {
          $group: {
            _id: null,
            totalBetAmount: { $sum: { $ifNull: ["$totalBetAmount", 0] } },
            totalWinAmount: { $sum: { $ifNull: ["$totalWinAmount", 0] } },
          },
        },
      ];

      const gcData = await new Parse.Query("GameCoins").aggregate(gcPipeline, { useMasterKey: true });
      const gc = gcData[0] || { totalBetAmount: 0, totalWinAmount: 0 };

      totalBetAmount += gc.totalBetAmount;
      totalWinAmount += gc.totalWinAmount;
    }

    // -------- Final Merged Totals --------
    const merged = {
      totalRecharge,
      totalRedeem,
      netAmount,
      totalBetAmount,
      totalWinAmount,
    };

    return merged;
  } catch (err) {
    console.error("Error in accountsSummary:", err);
    return {
      totalRecharge: 0,
      totalRedeem: 0,
      totalBetAmount: 0,
      totalWinAmount: 0,
      error: err.message || "Something went wrong",
    };
  }
};

